import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSsoRetryBounce } from '@kawai/app-shell';
import {
  MANAGEMENT_APP_LINKS,
  resolveFeatureAppUrl,
  resolvePortalUrl,
} from '@kawai/shared';
import { Button } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';
import { ROUTES } from '../routes.js';

export interface AuthGuardProps {
  children: ReactNode;
}

/**
 * F-02: tenant-fuel の管理ガード。
 *
 * - status='loading'                 : 「読み込み中…」
 * - status='signed-out'               : /signin へ
 * - status='signed-in' && !isAdmin    : 権限なしメッセージ
 * - status='signed-in' && isAdmin     : 子要素を描画
 *
 * feature ('fuel') 契約チェックは `<RequireFeature feature='fuel'>` 側で行う。
 */
// S-91 第2弾: 素リンク SSO 到着でセッションが無い場合の bounce 先と許可 referrer。
const PORTAL_URL = resolvePortalUrl(import.meta.env.MODE);
const SSO_REFERRER_ORIGINS: readonly string[] = [
  PORTAL_URL,
  ...MANAGEMENT_APP_LINKS.map(
    (l) => resolveFeatureAppUrl(l.feature, import.meta.env.MODE).url,
  ),
];

export function AuthGuard({ children }: AuthGuardProps) {
  const { status, isAdmin, signOut, email } = useAuth();
  const location = useLocation();

  // S-91 第2弾: 素リンク SSO 到着でセッション失効時は portal へ ?sso_retry bounce して
  // 自己修復する（明示サインアウトとの区別は hook 内で行う）。
  const ssoBounce = useSsoRetryBounce({
    feature: 'promo',
    portalUrl: PORTAL_URL,
    allowedReferrerOrigins: SSO_REFERRER_ORIGINS,
    status,
  });

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-full items-center justify-center text-sm text-slate-500"
      >
        読み込み中…
      </div>
    );
  }

  if (status === 'signed-out') {
    if (ssoBounce !== 'none') {
      return (
        <div
          role="status"
          aria-live="polite"
          className="flex min-h-full items-center justify-center text-sm text-slate-500"
        >
          読み込み中…
        </div>
      );
    }
    return <Navigate to={ROUTES.signIn} replace state={{ from: location.pathname }} />;
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-full items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm rounded-md border border-slate-200 bg-white p-6 text-center shadow-xs">
          <h1 className="text-base font-bold text-slate-900">権限がありません</h1>
          <p className="mt-2 text-sm text-slate-600">
            {email ?? 'このアカウント'} には管理者権限が付与されていません。
            <br />
            管理者にお問い合わせください。
          </p>
          <Button
            type="button"
            variant="dark"
            fullWidth
            onClick={() => {
              void signOut();
            }}
            className="mt-4"
          >
            サインアウト
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
